/**
 * A link into the page the visitor is already on (ADR-141 Amendment 1).
 *
 * The owner, 2026-09-24: "When the link points into the page, it should scroll
 * that page." A reply's links open in a new tab (markdown.ts), which is right
 * for every other page of the site and wrong for this one: the visitor would
 * be handed a second copy of the page they are reading, scrolled somewhere in
 * a tab they did not ask for. So a plain click on a link into THIS page finds
 * the part it names and scrolls there, in place.
 *
 * The address is the one shared-content built from the page (ADR-141 §1):
 * `#[id][:~:text=<words>[,<end>][,-<suffix>]]`, where the words are the first
 * line of a heading as the browser draws it and the suffix the words drawn
 * right after that line. So the part is found the way the browser would find
 * it — the first heading whose first line holds the words, followed by the
 * suffix — and then by the id, and it is scrolled to the middle of the window
 * and highlighted for a moment, as a browser shows a text fragment.
 *
 * Found nowhere — the page has changed since it was read, or the heading is
 * hidden — and nothing is taken over: the link opens as it always has, and the
 * browser has its own go at the address in the new tab.
 */

/** The part of a page a link names. */
export interface SectionTarget {
  /** The element id before `:~:`, decoded; '' when the address has none. */
  id: string;
  /** The text directive's words, decoded: its start, its end (a range) and its suffix. */
  start: string;
  end: string;
  suffix: string;
}

const DIRECTIVE_DELIMITER = ':~:';

function decode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return '';
  }
}

/** One page, whatever the host's `www.` and the path's trailing slash. */
function sameHost(a: string, b: string): boolean {
  const bare = (host: string) => host.toLowerCase().replace(/^www\./, '');
  return bare(a) === bare(b);
}

function samePath(a: string, b: string): boolean {
  const trim = (path: string) => path.replace(/\/+$/, '') || '/';
  return trim(a) === trim(b);
}

/**
 * The part of THIS page `href` names, or null when it names another page, or
 * this page and no part of it.
 *
 * `here` is the page's own address (`location.href`). The query is part of
 * which page it is — `?p=2` is another page — unless the link has none, in
 * which case the visitor's own (`utm_source`, a campaign id) does not make
 * the page they are on a different one.
 */
export function sectionOnThisPage(href: string, here: string): SectionTarget | null {
  let link: URL;
  let page: URL;
  try {
    link = new URL(href);
    page = new URL(here);
  } catch {
    return null;
  }
  if (link.protocol !== page.protocol || link.port !== page.port) return null;
  if (!sameHost(link.hostname, page.hostname) || !samePath(link.pathname, page.pathname)) return null;
  if (link.search && link.search !== page.search) return null;

  const fragment = link.hash.slice(1);
  if (!fragment) return null;
  const at = fragment.indexOf(DIRECTIVE_DELIMITER);
  const id = decode(at === -1 ? fragment : fragment.slice(0, at));
  let start = '';
  let end = '';
  let suffix = '';
  if (at !== -1) {
    for (const directive of fragment.slice(at + DIRECTIVE_DELIMITER.length).split('&')) {
      if (!directive.startsWith('text=')) continue;
      // [prefix-,]start[,end][,-suffix] — the crawler never writes a prefix.
      const parts = directive.slice('text='.length).split(',');
      if (parts.length > 1 && parts[0].endsWith('-')) parts.shift();
      if (parts.length > 1 && parts[parts.length - 1].startsWith('-')) {
        suffix = decode((parts.pop() as string).slice(1));
      }
      start = decode(parts[0] ?? '');
      end = decode(parts[1] ?? '');
      break;
    }
  }
  if (!id && !start) return null;
  return { id, start, end, suffix };
}

// ── Finding it on the page ───────────────────────────────────────

/** Where drawn text breaks onto a new line: no browser matches across one. */
const LINE_BREAKS = new Set([
  'BR', 'P', 'DIV', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'ASIDE', 'NAV', 'UL', 'OL', 'LI',
  'DL', 'DT', 'DD', 'TABLE', 'TR', 'TD', 'TH', 'BLOCKQUOTE', 'FIGURE', 'FIGCAPTION',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
]);
const UNDRAWN = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE']);
const WORD = /[\p{L}\p{N}_]/u;
const HAS_WORDS = /[\p{L}\p{N}]/u;

function collapse(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function norm(text: string): string {
  return collapse(text).toLowerCase();
}

/**
 * A heading's lines as a browser draws them — its text joined WITHOUT a
 * separator (`<span>Our</span><span>Services</span>` is "OurServices") and
 * broken at a `<br>` or a block. The crawler's `_rendered_lines`, on the
 * live page.
 */
function drawnLines(el: Element): string[] {
  const lines: string[] = [];
  let current = '';
  const walk = (node: Node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        current += child.nodeValue ?? '';
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      const tag = (child as Element).tagName;
      if (UNDRAWN.has(tag)) return;
      const breaks = LINE_BREAKS.has(tag);
      if (breaks) {
        lines.push(current);
        current = '';
      }
      if (tag !== 'BR') walk(child);
      if (breaks) {
        lines.push(current);
        current = '';
      }
    });
  };
  walk(el);
  lines.push(current);
  return lines.map(collapse).filter(Boolean);
}

/** Whether `line` holds `words` as whole words, in any case — as a browser matches. */
function holdsWords(line: string, words: string): boolean {
  const hay = norm(line);
  const needle = norm(words);
  if (!needle) return false;
  for (let from = 0; ; ) {
    const at = hay.indexOf(needle, from);
    if (at === -1) return false;
    const before = at === 0 ? '' : hay[at - 1];
    const after = hay[at + needle.length] ?? '';
    if (!WORD.test(before) && !WORD.test(after)) return true;
    from = at + 1;
  }
}

/** The first text drawn after `el`, white space collapsed. */
function textAfter(el: Element): string {
  const doc = el.ownerDocument;
  let last: Node = el;
  while (last.lastChild) last = last.lastChild;
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  walker.currentNode = last;
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const parent = node.parentElement;
    if (!parent || parent.closest('script,style,noscript,template')) continue;
    const text = collapse(node.nodeValue ?? '');
    if (text) return text;
  }
  return '';
}

/** The widget's own markup — a reply's `####` heading is not the page's. */
function inWidget(el: Element): boolean {
  return el.closest('#pweb-chat-root, [data-pwe]') !== null;
}

/** Whether the page draws `el` at all: a hidden heading is nowhere to scroll to. */
function drawn(el: Element): boolean {
  return el.getClientRects().length > 0;
}

/**
 * The element on this page `target` names, or null. The words first, then the
 * id — the order a browser tries a fragment in, so a site that renames an id
 * and keeps its heading still lands, and one that rewords a heading and keeps
 * its id does too.
 */
export function findSection(target: SectionTarget, doc: Document): Element | null {
  if (target.start) {
    for (const heading of Array.from(doc.querySelectorAll('h1, h2, h3, h4, h5, h6'))) {
      if (inWidget(heading) || !drawn(heading)) continue;
      const lines = drawnLines(heading);
      const first = lines.findIndex((line) => HAS_WORDS.test(line));
      if (first === -1) continue;
      const line = lines[first];
      if (!holdsWords(line, target.start) || (target.end && !holdsWords(line, target.end))) continue;
      if (target.suffix) {
        const after = first + 1 < lines.length ? lines[first + 1] : textAfter(heading);
        if (!norm(after).startsWith(norm(target.suffix))) continue;
      }
      return heading;
    }
  }
  if (target.id) {
    const el = doc.getElementById(target.id) ?? doc.getElementsByName(target.id)[0] ?? null;
    if (el && !inWidget(el) && drawn(el)) return el;
  }
  return null;
}

// ── Showing it ───────────────────────────────────────────────────

const HIGHLIGHT = 'pwe-section';
const HIGHLIGHT_STYLE_ID = 'pwe-section-highlight';
const HIGHLIGHT_MS = 4000;
let clearHighlight: number | undefined;

interface HighlightApi {
  highlights: { set(name: string, value: unknown): void; delete(name: string): void };
}

/**
 * Scroll `el` to the middle of the window — where a browser puts a text
 * fragment, clear of a sticky header — and highlight it for a moment.
 *
 * The highlight is the CSS Custom Highlight API's, which paints over the page
 * WITHOUT touching its DOM: this is a client's page, often a framework's, and
 * wrapping their heading in a `<mark>` could break what renders it. Where the
 * API is missing the section is scrolled to and not highlighted.
 */
export function showSection(el: Element, win: Window): void {
  const reduce = typeof win.matchMedia === 'function' && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });

  const css = (win as unknown as { CSS?: Partial<HighlightApi> }).CSS;
  const Highlight = (win as unknown as { Highlight?: new (range: Range) => unknown }).Highlight;
  if (!css?.highlights || typeof Highlight !== 'function') return;
  const doc = el.ownerDocument;
  if (!doc.getElementById(HIGHLIGHT_STYLE_ID)) {
    const style = doc.createElement('style');
    style.id = HIGHLIGHT_STYLE_ID;
    style.textContent = `::highlight(${HIGHLIGHT}) { background-color: rgba(255, 214, 0, 0.45); }`;
    doc.head.appendChild(style);
  }
  const range = doc.createRange();
  range.selectNodeContents(el);
  css.highlights.set(HIGHLIGHT, new Highlight(range));
  if (clearHighlight !== undefined) win.clearTimeout(clearHighlight);
  clearHighlight = win.setTimeout(() => css.highlights?.delete(HIGHLIGHT), HIGHLIGHT_MS);
}
